import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSettings } from "@/lib/SettingsContext";
import SmartLogo from "../SmartLogo";
import "./navbar.css";

const IconChevron = () => (
  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M3 4.5 6 8 9 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const NavDropdown: React.FC<{ label: string; items: { label: string; to: string; external?: boolean }[] }> = ({ label, items }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const location = useLocation();

  const cancel = () => { if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const schedule = () => { cancel(); closeTimer.current = window.setTimeout(() => { setOpen(false); closeTimer.current = null; }, 150); };
  const openNow = () => { cancel(); setOpen(true); };

  useEffect(() => { cancel(); setOpen(false); }, [location.pathname, location.search]);
  useEffect(() => () => cancel(), []);

  return (
    <div
      className={`iav-nv-drop ${open ? "is-open" : ""}`}
      onMouseEnter={openNow}
      onMouseLeave={schedule}
    >
      <button
        type="button"
        className="iav-nv-link iav-nv-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
      >
        {label}
        <span className="iav-nv-chev" aria-hidden><IconChevron /></span>
      </button>
      <div className="iav-nv-panel" role="menu">
        {items.map(it => (
          it.external
            ? <a key={it.to} href={it.to} target="_blank" rel="noopener noreferrer" className="iav-nv-panel-item" role="menuitem">{it.label}</a>
            : <Link key={it.to} to={it.to} className="iav-nv-panel-item" role="menuitem">{it.label}</Link>
        ))}
      </div>
    </div>
  );
};

const Navbar: React.FC = () => {
  const { settings } = useSettings();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Broadcast the drawer state on <body> so global CSS (responsive.css) can lift
  // the navbar above fixed hero social icons and hide overlay-bleed elements.
  useEffect(() => {
    document.body.classList.toggle("iav-nav-open", mobileOpen);
    return () => document.body.classList.remove("iav-nav-open");
  }, [mobileOpen]);

  const logoSrc = settings.logo_url || "";
  // Logo size now drives the header height (the bar grows to fit the logo, so a
  // bigger logo never overlaps the hero photo below). Kept backward-compatible
  // with the old logo_width value.
  const logoSize = parseInt(settings.logo_height || settings.logo_width || "150", 10);
  const logoNudgeX = parseInt(settings.logo_nudge_x || "0", 10);
  const logoNudgeY = parseInt(settings.logo_nudge_y || "0", 10);

  // Publish the header's height (logo size + vertical padding) to the document so
  // the hero can size itself to the remaining viewport (100vh − header).
  useEffect(() => {
    const h = logoSize + 34; // must match navbar.css .iav-nv-row min-height padding
    document.documentElement.style.setProperty("--iav-nav-h", `${h}px`);
    return () => { document.documentElement.style.removeProperty("--iav-nav-h"); };
  }, [logoSize]);

  return (
    <header className="iav-nv" data-testid="navbar" style={{ ["--iav-logo-size" as any]: `${logoSize}px` }}>
      <div className="iav-nv-row">
        {/* Logo (left) */}
        <Link to="/" className="iav-nv-logo" aria-label="Ideation AV — home" data-testid="nav-logo" style={{ transform: `translate(${logoNudgeX}px, ${logoNudgeY}px)` }}>
          {logoSrc ? (
            <SmartLogo src={logoSrc} alt="Ideation AV" height={logoSize} />
          ) : (
            <>
              <span className="iav-nv-logo-row1">IDEATION</span>
              <span className="iav-nv-logo-row2">AV</span>
            </>
          )}
        </Link>

        {/* Centre nav */}
        <nav className="iav-nv-centre" aria-label="Primary">
          <NavLink to="/" end className={({ isActive }) => `iav-nv-link ${isActive ? "is-active" : ""}`}>Home</NavLink>
          <NavLink to="/about" className={({ isActive }) => `iav-nv-link ${isActive ? "is-active" : ""}`}>About</NavLink>
          <NavDropdown label="Services" items={[
            { label: "All Services", to: "/services" },
            { label: "Audio Systems", to: "/services/audio" },
            { label: "Lighting Solutions", to: "/services/lighting" },
            { label: "LED Screens", to: "/services/led-video" },
            { label: "Projection Mapping", to: "/services/projection-mapping" },
            { label: "SFX & Laser", to: "/services/sfx-laser" },
            { label: "Rigging & Staging", to: "/services/rigging-staging" },
            { label: "Technical Drawings", to: "/services/technical-drawings" },
            { label: "3D Visualisation", to: "/services/3d-visualisation" },
          ]} />
          <NavDropdown label="Gallery" items={[
            { label: "All Projects", to: "/gallery" },
            { label: "Corporate Events", to: "/gallery?cat=corporate" },
            { label: "Live Concerts", to: "/gallery?cat=concerts" },
            { label: "Government & Gala", to: "/gallery?cat=gala" },
            { label: "Exhibitions", to: "/gallery?cat=exhibitions" },
            { label: "Social Events", to: "/gallery?cat=social" },
          ]} />
          <NavLink to="/case-studies" className={({ isActive }) => `iav-nv-link ${isActive ? "is-active" : ""}`} data-testid="nav-case-studies">Case Studies</NavLink>
          <NavDropdown label="Careers" items={[
            { label: "We're Hiring", to: "/careers/hiring" },
            { label: "Freelancers & Crew", to: "/careers/freelance" },
          ]} />
          <NavLink to="/contact" className={({ isActive }) => `iav-nv-link ${isActive ? "is-active" : ""}`}>Contact</NavLink>
        </nav>

        {/* CTA (right) */}
        <div className="iav-nv-cta">
          <Link to="/quote" className="iav-btn iav-btn-primary" data-testid="nav-quote-btn">
            <span className="iav-dots">··</span> Get a Quote
          </Link>
        </div>

        {/* Mobile burger */}
        <button className="iav-nv-burger" aria-label="Menu" aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)} data-testid="nav-burger">
          <span /><span /><span />
        </button>
      </div>

      {mobileOpen && (
        <div className="iav-nv-mobile" role="menu" data-testid="nav-mobile">
          <NavLink to="/" end className="iav-nv-mobile-link">Home</NavLink>
          <NavLink to="/about" className="iav-nv-mobile-link">About</NavLink>
          <NavLink to="/services" className="iav-nv-mobile-link">Services</NavLink>
          <NavLink to="/gallery" className="iav-nv-mobile-link">Gallery</NavLink>
          <NavLink to="/case-studies" className="iav-nv-mobile-link" data-testid="nav-mobile-case-studies">Case Studies</NavLink>
          <NavLink to="/careers/hiring" className="iav-nv-mobile-link">Careers</NavLink>
          <NavLink to="/contact" className="iav-nv-mobile-link">Contact</NavLink>
          <Link to="/quote" className="iav-btn iav-btn-primary" style={{ alignSelf: "flex-start", marginTop: 16 }}>
            <span className="iav-dots">··</span> Get a Quote
          </Link>
        </div>
      )}
    </header>
  );
};
export default Navbar;
